import { createContext, useCallback, useContext, useLayoutEffect, useEffect, useRef, type ReactNode } from 'react';
import { useI18n } from './i18n';

interface UnsavedChangesContextValue {
  markDirty: (source: symbol, dirty: boolean) => void;
  confirmDiscard: () => boolean;
}

const UnsavedChangesContext = createContext<UnsavedChangesContextValue | null>(null);

export function UnsavedChangesProvider({ children }: { children: ReactNode }) {
  const { locale } = useI18n();
  const sources = useRef(new Set<symbol>());

  const markDirty = useCallback((source: symbol, dirty: boolean) => {
    if (dirty) sources.current.add(source);
    else sources.current.delete(source);
  }, []);

  const confirmDiscard = useCallback(() => {
    if (sources.current.size === 0) return true;
    const confirmed = window.confirm(locale === 'zh-CN'
      ? '这页还有没有保存的修改。离开后这些修改会丢失，确定离开吗？'
      : 'You have unsaved changes. Leaving will discard them. Leave anyway?');
    if (confirmed) sources.current.clear();
    return confirmed;
  }, [locale]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (sources.current.size === 0) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  return <UnsavedChangesContext.Provider value={{ markDirty, confirmDiscard }}>{children}</UnsavedChangesContext.Provider>;
}

function useUnsavedChangesContext(): UnsavedChangesContextValue {
  const context = useContext(UnsavedChangesContext);
  if (!context) throw new Error('UnsavedChangesProvider is missing');
  return context;
}

export function useUnsavedChanges(dirty: boolean): () => void {
  const { markDirty } = useUnsavedChangesContext();
  const source = useRef(Symbol('unsaved-editor'));

  useLayoutEffect(() => {
    markDirty(source.current, dirty);
    return () => markDirty(source.current, false);
  }, [dirty, markDirty]);

  return useCallback(() => markDirty(source.current, false), [markDirty]);
}

export function useUnsavedChangesGuard() {
  return useUnsavedChangesContext().confirmDiscard;
}
